import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import ConnectyCube from 'connectycube';
import { config, tables } from '../config/environment';
import AdminLandingCard from '../components/AdminLandingCard';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  created_at: string;
  role?: string;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [cubeUser, setCubeUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Supabase 클라이언트 확인
      if (!supabase) {
        console.warn('Supabase client not initialized');
        navigate('/login');
        return;
      }

      // Supabase 세션 확인
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/login');
        return;
      }

      // 프로필 정보 가져오기 (환경에 따라 D_profiles 또는 P_profiles 테이블 사용)
      console.log('Using table:', tables.profiles);
      console.log('Environment:', config.isDevelopment ? 'Development' : 'Production');
      console.log('Session user ID:', session.user.id);
      
      const { data: profile, error: profileError } = await supabase
        .from(tables.profiles)
        .select('id, email, full_name, created_at, role')
        .eq('id', session.user.id)
        .single();
      
      if (profileError) {
        console.error('Error fetching profile:', profileError);
      }

      if (profile) {
        console.log('User profile:', profile);
        console.log('User role from DB:', profile.role);
        console.log('Type of role:', typeof profile.role);
        
        // Set user with explicit role
        const userWithRole = {
          ...profile,
          role: profile.role || 'user'  // Ensure role has a value
        };
        setUser(userWithRole);
        
        // Check if user is admin - handle potential string/null issues
        const roleValue = profile.role ? String(profile.role).toLowerCase().trim() : '';
        const adminStatus = roleValue === 'admin' || roleValue === 'super_admin';
        console.log('Normalized role:', roleValue);
        console.log('Is admin?', adminStatus);
        
        // TEMPORARY WORKAROUND: Force admin for specific email
        // Remove this after fixing the role issue
        const isHardcodedAdmin = profile.email === 'leewann@gmail.com';
        if (isHardcodedAdmin) {
          console.log('WORKAROUND: Forcing admin access for leewann@gmail.com');
        }
        
        setIsAdmin(adminStatus || isHardcodedAdmin);
        
        // Alternative check for debugging
        if (profile.role && (profile.role === 'admin' || profile.role === 'super_admin')) {
          console.log('Direct role check: User IS admin');
        } else {
          console.log('Direct role check: User is NOT admin, role is:', profile.role);
        }
      } else {
        console.log('No profile found for user:', session.user.id);
      }

      // ConnectyCube 사용자 정보 가져오기 (선택사항)
      try {
        // ConnectyCube 초기화 확인
        const appId = process.env.REACT_APP_CUBE_APP_ID;
        const authKey = process.env.REACT_APP_CUBE_AUTH_KEY;
        
        if (appId && authKey && ConnectyCube.users) {
          // 세션 생성 시도
          try {
            await ConnectyCube.createSession();
          } catch (sessionError) {
            // 세션이 이미 있을 수 있음
          }
          
          const users: any = await ConnectyCube.users.get({ 
            email: session.user.email 
          });
          if (users.items && users.items.length > 0) {
            setCubeUser(users.items[0]);
          }
        }
      } catch (cubeError) {
        console.warn('ConnectyCube 사용자 정보 가져오기 실패:', cubeError);
      }
    } catch (error) {
      console.error('인증 확인 오류:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // ConnectyCube 로그아웃
      try {
        await ConnectyCube.chat.disconnect();
        await ConnectyCube.logout();
      } catch (cubeError) {
        console.warn('ConnectyCube 로그아웃 실패:', cubeError);
      }

      // Supabase 로그아웃
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      {/* 헤더 */}
      <header className="bg-white/90 backdrop-blur-xl shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity cursor-pointer">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white text-xl font-bold">A</span>
              </div>
              <h1 className="text-xl font-bold text-gray-900">
                AivueBiz Dashboard
                {config.isDevelopment && (
                  <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-md">
                    {config.envName}
                  </span>
                )}
              </h1>
            </a>
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Admin button - shows when user has admin or super_admin role */}
              {(isAdmin || user?.role === 'admin' || user?.role === 'super_admin') && (
                <a 
                  href="/dashboard/mgmt"
                  className="inline-flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="hidden sm:inline">관리자 대시보드</span>
                  <span className="sm:hidden">관리자</span>
                </a>
              )}
              <button
                onClick={handleLogout}
                className="px-3 sm:px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors cursor-pointer text-sm sm:text-base"
              >
                로그아웃
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 환영 메시지 */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            환영합니다, {user?.full_name || user?.email}님! 👋
          </h2>
          <p className="text-gray-600">
            AivueBiz 웹 대시보드에 로그인하셨습니다.
          </p>
          {isAdmin ? (
            <p className="text-sm text-red-600 mt-3 font-medium">
              🛡️ 관리자 권한이 활성화되었습니다. (role: {user?.role})
            </p>
          ) : (
            <p className="text-sm text-gray-500 mt-3">
              현재 역할: {user?.role || 'user'}
            </p>
          )}
        </div>

        {/* Admin Landing Card - 관리자만 표시 */}
        {isAdmin && <AdminLandingCard isKorean={true} />}

        {/* 사용자 정보 카드 */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Supabase 프로필 */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">프로필 정보</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-500">이메일</span>
                <p className="text-gray-900 font-medium">{user?.email}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">이름</span>
                <p className="text-gray-900 font-medium">{user?.full_name || '미설정'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">가입일</span>
                <p className="text-gray-900 font-medium">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('ko-KR') : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* ConnectyCube 정보 */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">채팅 상태</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-500">ConnectyCube ID</span>
                <p className="text-gray-900 font-medium">{cubeUser?.id || '연결되지 않음'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">연결 상태</span>
                <p className="text-gray-900 font-medium">
                  {cubeUser ? (
                    <span className="inline-flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      온라인
                    </span>
                  ) : (
                    <span className="inline-flex items-center">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                      오프라인
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 기능 안내 */}
        <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl shadow-lg p-8 text-white">
          <h3 className="text-2xl font-bold mb-4">더 많은 기능을 이용하세요!</h3>
          <p className="text-white/90 mb-6">
            채팅, 영상통화, 실시간 번역 등 AivueBiz의 모든 기능을 이용하려면 데스크톱 앱을 사용하세요.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="text-3xl mb-2">💬</div>
              <h4 className="font-semibold mb-1">실시간 채팅</h4>
              <p className="text-sm text-white/80">1:1 및 그룹 채팅</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="text-3xl mb-2">📹</div>
              <h4 className="font-semibold mb-1">영상통화</h4>
              <p className="text-sm text-white/80">HD 화질 영상통화</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="text-3xl mb-2">🌍</div>
              <h4 className="font-semibold mb-1">실시간 번역</h4>
              <p className="text-sm text-white/80">30개 이상 언어 지원</p>
            </div>
          </div>
          <a
            href="/download"
            className="inline-flex items-center px-6 py-3 bg-white text-primary-700 rounded-xl font-semibold hover:shadow-xl transition-all duration-300 cursor-pointer"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            데스크톱 앱 다운로드
          </a>
        </div>

        {/* 빠른 링크 */}
        <div className="mt-8 grid md:grid-cols-4 gap-4">
          <a href="/" className="bg-white rounded-xl p-4 text-center hover:shadow-lg transition-all duration-300 cursor-pointer">
            <div className="text-2xl mb-2">🏠</div>
            <p className="text-gray-700 font-medium">홈페이지</p>
          </a>
          <a href="/download" className="bg-white rounded-xl p-4 text-center hover:shadow-lg transition-all duration-300 cursor-pointer">
            <div className="text-2xl mb-2">⬇️</div>
            <p className="text-gray-700 font-medium">앱 다운로드</p>
          </a>
          <a href="/support" className="bg-white rounded-xl p-4 text-center hover:shadow-lg transition-all duration-300 cursor-pointer">
            <div className="text-2xl mb-2">💬</div>
            <p className="text-gray-700 font-medium">고객지원</p>
          </a>
          <a href="/settings" className="bg-white rounded-xl p-4 text-center hover:shadow-lg transition-all duration-300 cursor-pointer">
            <div className="text-2xl mb-2">⚙️</div>
            <p className="text-gray-700 font-medium">설정</p>
          </a>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;